import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import {
  faShoppingCart,
  faWallet,
  faChartLine,
  faDesktop,
} from '@fortawesome/free-solid-svg-icons';
import { useMerchantData } from '../../../hooks/useMerchantData';
import { useActiveStoreId } from '../../Stores/hooks';
import { useSalesData } from './hooks/useSalesData';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import {
  Container,
  HeaderRow,
  StatsGrid,
  ChartsRow,
  StoreSwitcherWrap,
  PlatformFilterRow,
} from './Styles';
import StoreSwitcher from '../../../commonComponents/StoreSwitcher';
import Greeting from './Greeting';
import StatCard from './StatCard';
import PlatformFilterDropdown from './PlatformFilterDropdown';
import OrdersSummaryCard from './OrdersSummaryCard';
import RevenueCard from './RevenueCard';
import OrdersByHourCard from './OrdersByHourCard';
import OrderChannelSplitCard from './OrderChannelSplitCard';
import {
  MOCK_ORDERS_TODAY,
  MOCK_TODAY_SALES,
  MOCK_ORDER_STATUS_BREAKDOWN,
  MOCK_REVENUE_LABELS,
  MOCK_REVENUE_SERIES,
  MOCK_ORDERS_BY_HOUR,
  MOCK_ORDER_CHANNEL_SPLIT,
} from './Constant';

const MerchantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const { data: merchantData, isLoading: isMerchantLoading } = useMerchantData();
  const activeStoreId = useActiveStoreId();
  const { data: sales, isLoading: isSalesLoading } = useSalesData(activeStoreId);
  const [platform, setPlatform] = useState('all');

  const deviceCount = merchantData?.devices?.length || 0;

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderRow>
            <Greeting name={merchantData?.name || ''} />

            <StoreSwitcherWrap>
              <StoreSwitcher />
            </StoreSwitcherWrap>
          </HeaderRow>

          <PlatformFilterRow>
            <PlatformFilterDropdown value={platform} onChange={setPlatform} />
          </PlatformFilterRow>

          <StatsGrid>
            <StatCard
              title="Today's Sales"
              value={`$${MOCK_TODAY_SALES}`}
              icon={faWallet}
              color="blue"
              onClick={() => navigate('/sales-analytics')}
              trend={12}
              trendLabel="vs yesterday"
            />
            <StatCard
              title="Total Sales"
              value={`$${sales || 0}`}
              icon={faChartLine}
              color="orange"
              onClick={() => navigate('/sales-analytics')}
              trend={18}
              trendLabel="this month"
              loading={isSalesLoading}
            />
            <StatCard
              title="Orders Today"
              value={MOCK_ORDERS_TODAY}
              icon={faShoppingCart}
              color="purple"
              onClick={() => navigate('/orders')}
              trend={12}
              trendLabel="vs last week"
            />
            <StatCard
              title="Active Devices"
              value={deviceCount}
              icon={faDesktop}
              color="green"
              onClick={() => navigate('/activesessionanddevices')}
              trend={5}
              trendLabel="vs last week"
              loading={isMerchantLoading}
            />
          </StatsGrid>

          <ChartsRow>
            <OrdersSummaryCard
              newOrders={MOCK_ORDERS_TODAY}
              breakdown={MOCK_ORDER_STATUS_BREAKDOWN}
              isDarkMode={isDarkMode}
            />
            <RevenueCard labels={MOCK_REVENUE_LABELS} series={MOCK_REVENUE_SERIES} isDarkMode={isDarkMode} />
          </ChartsRow>

          <ChartsRow>
            <OrdersByHourCard data={MOCK_ORDERS_BY_HOUR} isDarkMode={isDarkMode} />
            <OrderChannelSplitCard data={MOCK_ORDER_CHANNEL_SPLIT} isDarkMode={isDarkMode} />
          </ChartsRow>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default MerchantDashboard;
